import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MARKETING_NS } from '../../i18n/marketingSegment';
import { BRAND_CHANGED_EVENT, brandDnaService } from '../../services/brandDnaService';
import type { BrandDna } from '../../types/brandDna';
import { CheckIcon, ChevronDownIcon } from '../icons/Icons';

const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || 'S';

const hostOf = (url: string): string => {
  try {
    return new URL(url.includes('://') ? url : `https://${url}`).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

/** Logo do DNA que cabe num quadrado: símbolo/ícone primeiro; depois o wordmark. */
const BrandMark: React.FC<{ brand: BrandDna; size?: number }> = ({ brand, size = 34 }) => {
  const logos = brand.profile.logos ?? [];
  const logo = logos.find((l) => l.variant === 'symbol' || l.variant === 'icon') ?? logos[0];
  const [broken, setBroken] = useState(false);
  const box = { width: size, height: size };

  if (logo && !broken) {
    return (
      <span
        style={box}
        className={`rounded-[10px] grid place-items-center overflow-hidden flex-none border border-snapfy-border ${logo.background === 'dark' ? 'bg-[#141218]' : 'bg-white'}`}
      >
        <img src={logo.url} alt="" onError={() => setBroken(true)} className="max-w-[80%] max-h-[80%] object-contain" />
      </span>
    );
  }
  return (
    <span style={box} className="rounded-[10px] grid place-items-center bg-snapfy-gradient text-white text-[13px] font-bold flex-none">
      {initialsOf(brand.name)}
    </span>
  );
};

/**
 * Bloco do topo do menu do Marketing: a marca (cliente) atual — logo do DNA,
 * nome e o site — e, ao clicar, o seletor de marcas com o estado do DNA de
 * cada uma e "+ Novo cliente". Trocar aqui vale para todas as telas do
 * Marketing (brandDnaService.switchTo: a tela aberta recarrega na marca nova).
 * Acompanha a marca que as telas abrem (BRAND_CHANGED_EVENT).
 */
const BrandSwitcher: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => {
  const { t } = useTranslation(MARKETING_NS);
  const [brands, setBrands] = useState<BrandDna[] | null>(null);
  const [currentId, setCurrentId] = useState<number | null>(() => brandDnaService.getCurrentId());
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const brandsRef = useRef<BrandDna[] | null>(null);
  brandsRef.current = brands;

  const load = useCallback(() => {
    brandDnaService
      .list()
      .then(setBrands)
      .catch(() => setBrands((prev) => prev ?? []));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Marca aberta por uma tela (ou criada agora): acompanha e, se for nova, recarrega a lista.
  useEffect(() => {
    const handle = (e: Event) => {
      const id = Number((e as CustomEvent<number>).detail) || null;
      setCurrentId(id);
      const list = brandsRef.current;
      if (id && list && !list.some((b) => b.id === id)) load();
    };
    window.addEventListener(BRAND_CHANGED_EVENT, handle);
    return () => window.removeEventListener(BRAND_CHANGED_EVENT, handle);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const current = brands?.find((b) => b.id === currentId) ?? brands?.[0] ?? null;

  const handlePick = (brand: BrandDna) => {
    setOpen(false);
    if (brand.id !== current?.id) brandDnaService.switchTo(brand.id);
    setCurrentId(brand.id);
    onNavigate?.();
  };

  const statusLabel = (b: BrandDna) =>
    b.status === 'ready' ? t('dna.completeness', { value: b.completeness }) : b.status === 'analyzing' ? t('sidebar.brand.analyzing') : t('sidebar.brand.failed');

  // Sem marca ainda: o bloco convida a criar a primeira.
  if (brands && brands.length === 0) {
    return (
      <Link
        to="/marketing/dna/novo"
        onClick={onNavigate}
        className="mb-3 flex items-center gap-2.5 p-2 rounded-2xl border border-dashed border-snapfy-border-strong bg-snapfy-inner flex-shrink-0 hover:border-snapfy-primary"
      >
        <span className="w-[34px] h-[34px] rounded-[10px] grid place-items-center bg-snapfy-primary-20 text-snapfy-accent-soft text-[18px] flex-none">＋</span>
        <span className="min-w-0 flex flex-col gap-0.5">
          <span className="text-[13.5px] font-semibold leading-none text-snapfy-text-primary truncate">{t('sidebar.brand.createFirst')}</span>
          <span className="text-[11px] leading-none text-snapfy-text-secondary">{t('sidebar.brand.createFirstHint')}</span>
        </span>
      </Link>
    );
  }

  return (
    <div ref={ref} className="relative mb-3 flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={!current}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={t('sidebar.brand.switch')}
        className={`w-full flex items-center gap-2.5 p-2 rounded-2xl border bg-snapfy-inner text-left transition-colors ${
          open ? 'border-snapfy-primary' : 'border-snapfy-border hover:border-snapfy-border-strong'
        }`}
      >
        {current ? (
          <BrandMark brand={current} />
        ) : (
          <span className="w-[34px] h-[34px] rounded-[10px] bg-snapfy-surface animate-pulse flex-none" />
        )}
        <span className="min-w-0 flex-1 flex flex-col gap-0.5">
          <span className="text-[13.5px] font-semibold leading-none text-snapfy-text-primary truncate">{current?.name ?? t('sidebar.brand.loading')}</span>
          <span className="text-[11px] leading-none text-snapfy-text-secondary truncate">
            {current ? (current.siteUrl ? hostOf(current.siteUrl) : statusLabel(current)) : t('sidebar.workspaceSubtitle')}
          </span>
        </span>
        <ChevronDownIcon className={`w-4 h-4 shrink-0 text-snapfy-text-secondary transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && brands && (
        <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-30 p-1.5 rounded-2xl border border-snapfy-border bg-snapfy-surface shadow-modal backdrop-blur-xl">
          <span className="block px-2.5 pt-1.5 pb-1 font-mono text-[10px] uppercase tracking-[0.08em] text-snapfy-text-secondary">{t('sidebar.brand.title')}</span>
          <ul id={menuId} role="listbox" aria-label={t('sidebar.brand.title')} className="m-0 p-0 list-none max-h-[320px] overflow-y-auto custom-scrollbar">
            {brands.map((b) => {
              const selected = b.id === current?.id;
              return (
                <li key={b.id} role="option" aria-selected={selected}>
                  <button
                    type="button"
                    onClick={() => handlePick(b)}
                    className={`w-full flex items-center gap-2.5 p-2 rounded-xl text-left transition-colors ${selected ? 'bg-snapfy-primary-20' : 'hover:bg-snapfy-inner'}`}
                  >
                    <BrandMark brand={b} size={28} />
                    <span className="min-w-0 flex-1 flex flex-col gap-0.5">
                      <span className="text-[13px] font-medium leading-tight text-snapfy-text-primary truncate">{b.name}</span>
                      <span className={`text-[11px] leading-none ${b.status === 'failed' ? 'text-snapfy-error' : 'text-snapfy-text-secondary'}`}>{statusLabel(b)}</span>
                    </span>
                    {selected && <CheckIcon className="w-4 h-4 shrink-0 text-snapfy-accent-soft" />}
                  </button>
                </li>
              );
            })}
          </ul>
          <Link
            to="/marketing/dna/novo"
            onClick={() => {
              setOpen(false);
              onNavigate?.();
            }}
            className="mt-1 flex items-center gap-2 px-2.5 py-2 rounded-xl border-t border-snapfy-border text-[12.5px] font-semibold text-snapfy-accent-soft hover:bg-snapfy-inner"
          >
            {t('sidebar.brand.newClient')}
          </Link>
        </div>
      )}
    </div>
  );
};

export default BrandSwitcher;
